/**
 * Phase 14 — master containers. A master is a container row of kind MASTER
 * bound to a CAMPAIGN GROUP (the SMSWiser campaigns sharing one tracker link —
 * derived live from its representative campaign); followers inherit each
 * SECTION below unless they mark it "own". The campaign, schedule, slots and
 * list groups are never a section: they stay per container by definition.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { TrackerEnum } from "../../trackers/tracker-registry";
import type { DropGenAutoSendConfig } from "../autosend/autosend.types";
import type {
  DropGenCreativeDetail,
  DropGenGatewayDetail,
} from "../api.types";
import type {
  ShortlinkClickIdStructure,
  ShortlinkDomainScheme,
} from "../../smswiser/shortlink-options";
import type { DropGenFailMode } from "../types";
import type { DropGenVerdictDetail } from "../verdict.types";
import type { DropGenTextPolicy } from "../text-ranking/text-policy";

export const DROP_GEN_CONTAINER_KINDS = ["CONTAINER", "MASTER", "ROUTE_TEST"] as const;
export type DropGenContainerKind = (typeof DROP_GEN_CONTAINER_KINDS)[number];

/** The inheritable sections, each its own "Use master | Own settings" switch. */
export const DROP_GEN_SECTIONS = [
  "gatewayStrategy",
  "gatewayPool",
  "creative",
  "options",
  "verdict",
  "hourOptimizer",
  "amount",
  "failMode",
  /** Phase 20 — the group's auto-send; "own" = a layer ON TOP, never instead. */
  "autoSend",
] as const;
export type DropGenSection = (typeof DROP_GEN_SECTIONS)[number];

/** true = the container's own values win; missing = the master's. */
export type DropGenOwnSections = Partial<Record<DropGenSection, boolean>>;

/** Read-time facts about campaign groups — never block generation. */
export const DROP_GEN_GROUP_WARNINGS = [
  /** A follower's campaign was re-linked out of its master config's group. */
  "campaign_outside_group",
  /** The master config's representative campaign left the catalog. */
  "group_empty",
  /** Two master configs' groups converged after a re-link. */
  "group_shared",
] as const;
export type DropGenGroupWarning = (typeof DROP_GEN_GROUP_WARNINGS)[number];

export interface DropGenMasterRef {
  id: string;
  name: string;
  color: string;
  /** The representative campaign — the group is derived from its tracker link. */
  campaignWiserId: number;
  /** The SMSWiser campaign group key of that campaign, live. */
  campaignGroupKey: string;
}

/** A campaign group of the plan's country, as the board offers it. */
export interface DropGenCampaignGroup {
  key: string;
  label: string;
  fullLabel: string;
  trafficProvider: TrackerEnum;
  trafficCampaignId: string | null;
  campaigns: Array<{ wiserId: number; name: string }>;
  /** Its master config, if any. */
  masterId: string | null;
}

export interface DropGenMasterSummary extends DropGenMasterRef {
  groupWarnings: DropGenGroupWarning[];
  /** Phase 20 — the campaign group's auto-send (every container runs under it). */
  autoSend: DropGenAutoSendConfig;
  followerCount: number;
  defaultAmount: number;
  failMode: DropGenFailMode;
  scalingEnabled: boolean;
  verdictEnabled: boolean;
  hourOptimizerOn: boolean;
  poolCount: number;
  textGroupCount: number;
  hasSenderGroup: boolean;
  /** The rules panel's tiles — what each section actually holds. */
  strategy: string | null;
  textMode: string | null;
  assignment: string | null;
  /** Phase 12 — the text ranking's levers (share 0 = drafts untouched). */
  textRanking: { provenPerDrop: number; aiPerDrop: number; topN: number; leanGroups: boolean; policy?: DropGenTextPolicy };
  forceSend: boolean;
  shortLinkTldValue: string | null;
  clickIdStructure: ShortlinkClickIdStructure;
  domainScheme: ShortlinkDomainScheme;
  batches: boolean;
  aiOptimization: boolean;
  lookbackPreset: string | null;
  maxExtraSlots: number | null;
}

/** The container as every reader sees it after inheritance is resolved. */
export interface DropGenEffectiveConfig {
  /** Always the container's OWN campaign — never inherited. */
  campaignWiserId: number;
  defaultAmount: number;
  failMode: DropGenFailMode;
  scalingEnabled: boolean;
  hourOptimizerJson: Record<string, unknown> | null;
  gateway: DropGenGatewayDetail | null;
  creative: DropGenCreativeDetail | null;
  verdict: DropGenVerdictDetail | null;
  /** Where each section came from. */
  sources: Record<DropGenSection, "own" | "master">;
}
